<template>
  <b-container>
    <b-row>
      <b-col cols="12" class="mb-3">
        <h1>Welcome to ZuulOS Portal, {{ firstName }}!</h1>
        <p v-if="hubAdmin || kitchenAdmin">
          The ZuulOS Portal is your homebase for menu management, order details,
          and reporting. Update your brands, items, and modifiers, or bulk
          export and import menu information in the Menus section, then head
          over to Orders to view or export all current and past orders. Please
          reach out to
          <a href="mailto:hello@zuul.com" target="_blank">hello@zuul.com</a> if
          you have any questions or feedback.
        </p>
        <template v-else>
          <p>
            The ZuulOS Portal is your homebase for menu management. Update your
            brands, menus, items, and modifiers, or bulk export and import menu
            information in the Menus section. Please reach out to
            <a href="mailto:hello@zuul.com" target="_blank">hello@zuul.com</a>
            if you have any questions or feedback.
          </p>
          <h5>Menus</h5>
          <div class="dashboard-list">
            <ul class="pl-0">
              <li v-for="(link, index) in navigationLinks" :key="index">
                <router-link :to="link.href">
                  {{ link.title }}
                </router-link>
              </li>
            </ul>
          </div>
        </template>
      </b-col>
    </b-row>
    <b-row v-if="hubAdmin || kitchenAdmin" class="mb-2">
      <b-col sm="12" md="6">
        <span class="icon d-inline-block">
          <img
            :src="asset('/imgs/icons/shopping-bag-red.svg')"
            alt="recent orders"
            class="pb-1 pr-2"
        /></span>
        <h5 class="d-inline-block mr-4">Recent Orders</h5>
        <router-link
          v-if="hubAdmin"
          router-link
          class="d-inline-block"
          to="/orders"
          >see all orders ></router-link
        >
        <router-link
          v-else-if="kitchenAdmin"
          router-link
          class="d-inline-block"
          to="/kitchen-orders"
          >see all orders ></router-link
        >
      </b-col>
      <b-col sm="12" md="6">
        <custom-date-picker
          :initial-range="range"
          class="mr-0 float-md-right"
          @rangeChange="onRangeChange"
        />
      </b-col>
    </b-row>
    <b-row v-if="hubAdmin" class="overflow-auto">
      <b-col cols="12">
        <b-table
          hover
          no-local-sorting
          small
          :fields="orderFields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onOrdersRowClicked"
        >
          <template #cell(status)="row">
            {{ row.item.status | statusFormat }}
          </template>
          <template #cell(fulfillment_time)="row">
            {{ row.item.fulfillment_time | formatDateTime }}
          </template>
          <template #cell(address)="row">
            {{ row.item.address | formatAddressStreet }}
          </template>
          <template #cell(user)="row">
            {{ row.item.user.first_name }}
            {{ row.item.user.last_name }}
          </template>
          <template #cell(order_total)="row">
            {{ (row.item.order_total / 100) | currency }}
          </template>
          <template #cell(facility)="row">
            {{ row.item.facility.name }}
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row v-else-if="kitchenAdmin">
      <b-col cols="12">
        <b-table
          hover
          no-local-sorting
          small
          :fields="kitchenOrderFields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onKitchenOrdersRowClicked"
        >
          <template #cell(status)="row">
            {{ row.item.status | capitalize }}
          </template>
          <template #cell(fulfillment_time)="row">
            {{ row.item.network_order.fulfillment_time | formatDateTime }}
          </template>
          <template #cell(address)="row">
            {{ row.item.network_order.address | formatAddressStreet }}
          </template>
          <template #cell(fulfillment_center)="row">
            {{ row.item.fulfillment_center.name }}
          </template>
          <template #cell(order_total)="row">
            {{ (row.item.order_total / 100) | currency }}
          </template>
          <template #cell(facility)="row">
            {{ row.item.network_order.facility.name }}
          </template>
        </b-table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import ResourceAPI from '../services/ResourceAPI';

export default {
  name: 'App',
  data() {
    return {
      range: {
        start: null,
        end: null,
      },
      data: [],
      sort: null,
      dir: null,
      busy: false,
      navigationLinks: [
        {
          title: 'Brands',
          href: '/brands',
        },
        {
          title: 'Menus',
          href: '/menus',
        },
        {
          title: 'Items',
          href: '/items',
        },
        {
          title: 'Modifiers',
          href: '/modifiers',
        },
        {
          title: 'Imports & Exports',
          href: '/imports',
        },
      ],
      orderFields: [
        {
          key: 'number',
          label: 'Order ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'fulfillment_time',
          label: 'Fulfillment Time',
          sortable: true,
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
        },
        {
          key: 'user',
          label: 'Customer',
          sortable: true,
        },
        {
          key: 'order_total',
          label: 'Customer Total',
          sortable: true,
        },
        {
          key: 'facility',
          label: 'Hub',
          sortable: true,
        },
      ],
      kitchenOrderFields: [
        {
          key: 'number',
          label: 'Order ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'fulfillment_time',
          label: 'Fulfillment Time',
          sortable: true,
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
        },
        {
          key: 'fulfillment_center',
          label: 'Kitchen',
          sortable: true,
        },
        {
          key: 'order_total',
          label: 'Kitchen Total',
          sortable: true,
        },
        {
          key: 'facility',
          label: 'Hub',
          sortable: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', [
      'isAuthenticated',
      'firstName',
      'kitchenAdmin',
      'hubAdmin',
      'brandAdmin',
      'hubCount',
      'kitchenCount',
    ]),
  },
  watch: {
    isAuthenticated(val) {
      if (val) {
        this.loadResults();
      }
    },
    hubCount(val) {
      if (val <= 1) {
        this.removeTableColumn('facility');
      }
    },
    kitchenCount(val) {
      if (val <= 1) {
        this.removeTableColumn('fulfillment_center');
      }
    },
  },
  mounted() {
    if (this.isAuthenticated) {
      this.loadResults();
    }
    if (this.hubCount <= 1) {
      this.removeTableColumn('hub');
    }
    if (this.kitchenCount <= 1) {
      this.removeTableColumn('fulfillment_center');
    }
  },
  methods: {
    removeTableColumn(key) {
      let index = this.orderFields.findIndex((item) => item.key === key);
      this.orderFields.splice(index, 1);
      index = this.kitchenOrderFields.findIndex((item) => item.key === key);
      this.kitchenOrderFields.splice(index, 1);
    },
    onSortingChanged(ctx) {
      this.sort = ctx.sortBy;
      this.dir = ctx.sortDesc ? 'DESC' : 'ASC';
      this.loadResults();
    },
    onRangeChange(range) {
      this.range = {
        ...range,
      };
      this.loadResults();
    },
    loadResults() {
      if (this.hubAdmin) {
        this.loadOrders();
      } else if (this.kitchenAdmin) {
        this.loadKitchenOrders();
      }
    },
    loadOrders() {
      const me = this;

      me.busy = true;

      return ResourceAPI.getRecords('network-orders', {
        limit: 10,
        page: 1,
        sort: me.sort,
        dir: me.dir,
        with: 'address,facility,user',
        startDate: me.formatDateFilter(me.range.start),
        endDate: me.formatDateFilter(me.range.end),
      })
        .then((data) => {
          me.rows = data.total;
          me.data = data.data;
          me.busy = false;
        })
        .catch((error) => alert(error));
    },
    onOrdersRowClicked(record) {
      this.$router.push({
        name: 'order',
        params: { id: record.id },
      });
    },
    loadKitchenOrders() {
      const me = this;

      me.busy = true;

      return ResourceAPI.getRecords('fulfillment-center-orders', {
        limit: 10,
        page: 1,
        sort: me.sort,
        dir: me.dir,
        with: 'fulfillmentCenter,networkOrder.address,networkOrder.facility',
        startDate: me.formatDateFilter(me.range.start),
        endDate: me.formatDateFilter(me.range.end),
      })
        .then((data) => {
          me.rows = data.total;
          me.data = data.data;
          me.busy = false;
        })
        .catch((error) => alert(error));
    },
    onKitchenOrdersRowClicked(record) {
      this.$router.push({
        name: 'kitchen-order',
        params: { id: record.id },
      });
    },
  },
};
</script>
